import { css } from '@emotion/react';
import * as React from 'react';

import { CheckMark } from '~src/icons';
import { colors } from '~src/theme/colors';
import { ThemeModel } from '~src/theme/theme';
import { isTouchDevice } from '~src/utils/browser';

const isTouch = isTouchDevice();

interface Props {
  checked: boolean;
  /**  Pixels expected. Expands tappable area without affecting the layout */
  addedTappableAreaSize?: string;
  onChange: (value: boolean) => void;
  labelId: string;
  disabled?: boolean;
}

export const Checkbox: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  addedTappableAreaSize = '0px',
  checked,
  onChange,
  labelId,
  disabled,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onChange(!checked);
    }
  };

  return (
    <label css={styles.label}>
      <div css={{ position: 'relative' }}>
        <input
          id={labelId}
          type="checkbox"
          css={styles.input}
          checked={checked}
          onChange={() => {
            onChange(!checked);
          }}
          disabled={disabled}
          onKeyDown={handleKeyDown}
        />
        <span css={styles.checkmark}>
          <CheckMark />
        </span>
        {isTouch && <span css={styles.tappableArea(addedTappableAreaSize)} />}
      </div>
      {children}
    </label>
  );
};

const styles = {
  label: css({ cursor: 'pointer' }),
  input: (theme: ThemeModel) =>
    css({
      opacity: 0,
      position: 'absolute',
      cursor: 'pointer',
      '&:hover+span': {
        borderColor: colors.neutral75,
      },
      '&:checked+span': {
        background: theme.accent48,
        borderColor: theme.accent48,
      },
      '&.focus-visible+span': {
        outline: '3px solid rgb(0, 160, 255)',
      },
    }),
  checkmark: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    background: 'white',
    border: `1px solid ${colors.neutral80}`,
    borderRadius: '2px',
    cursor: 'pointer',
    svg: {
      width: '12px',
      height: '12px',
    },
  }),
  tappableArea: (addedTappableAreaSize: string) =>
    css({
      position: 'absolute',
      width: `calc(100% + ${addedTappableAreaSize})`,
      height: `calc(100% + ${addedTappableAreaSize})`,
      top: `calc(-${addedTappableAreaSize}/2)`,
      left: `calc(-${addedTappableAreaSize}/2)`,
    }),
};
